import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/features/language';
import { Drawer } from '@base-ui/react/drawer';
import cn from 'classnames';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuList } from '@/entities/sidebar';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user';
import { Bell } from '@/entities/notification';
import { brand } from '@/brand';
import { setMobileSidebar } from '@/features/sidebar';
import { Icon } from '@/shared/IconCollecion';
import { useLogout } from '@/features/auth';
import { ProfitInfo } from '@/entities/profit';

// Drawer справа (<lg). Лэйаут единый для всех брендов (за основу — wapclick,
// у которого он уже был): сверху аватар+юзернейм (слева) и Bell (справа, на
// месте бывшего заголовка «Меню»), ниже блок «За сегодня» + «Кошельки»
// (ProfitInfo variant=menu, как в десктоп-хедере), затем MenuList и Logout.
// От темы зависят ТОЛЬКО цвета (через токены content-*/line-*/surface-*).
// Единственное структурное ветвление — колокольчик: его наличие задаёт бренд
// (brand.features.notifications), а не тема.
//
// Адаптации:
// - logoutProcess → useLogout hook + hard navigate на /login.
// - User-link ведёт на /settings.

const MobileSidebar = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const logout = useLogout();

  const isMobileSidebarOpen = useAppSelector((state) => state.sidebar.isMobileSidebarOpen);
  const isProfileActive = location.pathname === '/settings';

  const logoutClickHandler = () => {
    dispatch(setMobileSidebar(false));
    logout();
    navigate('/login', { replace: true });
  };

  // Любая навигация из шторки должна её закрыть (пункты меню гасят себя сами
  // внутри MenuItem).
  const closeSidebar = () => {
    dispatch(setMobileSidebar(false));
  };

  const { t } = useTranslation('common');

  return (
    <Drawer.Root
      open={isMobileSidebarOpen}
      onOpenChange={(open) => dispatch(setMobileSidebar(open))}
      swipeDirection="right"
      // modal=false отключает body scroll-lock (см. NotificationsDrawer.tsx).
      modal={false}
    >
      <Drawer.Portal>
        <Drawer.Backdrop className="fixed inset-0 z-50 bg-overlay-strong transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 lg:hidden" />

        <Drawer.Popup
          data-testid="mobile-sidebar-drawer"
          className="fixed inset-y-0 right-0 z-50 flex w-[280px] max-w-full flex-col bg-surface-muted outline-none transition-transform duration-200 ease-out data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full lg:hidden"
        >
          <Drawer.Content className="flex min-h-0 flex-1 flex-col overflow-y-auto py-4 pl-2 pr-4">
            {/* Правый верхний угол шторки — её «шапка»: язык рядом с
                колокольчиком, как в десктопной шапке. */}
            <div className="absolute right-4 top-4 flex items-center gap-3">
              {brand.features.notifications && <Bell />}
              <LanguageSwitcher />
            </div>

            {brand.layout.sidebarProfile === 'link' && (
              <Link
                onClick={closeSidebar}
                to="/settings"
                className={cn(
                  'mb-4 flex w-[calc(100%-2rem)] flex-shrink-0 items-center justify-start truncate py-1',
                  isProfileActive
                    ? 'text-content-accent lg:text-content-inverse-accent'
                    : 'text-content-primary hover:text-content-accent lg:text-content-inverse-muted lg:hover:text-content-inverse-accent',
                )}
              >
                <div className="flex flex-1 items-center gap-2 truncate pl-2">
                  <Icon name="User_Big" className="size-6 shrink-0" color="currentColor" />
                  <div className="mr-auto max-w-[calc(100%-1.5rem)] truncate font-bold">
                    <span>{user ? user.username : 'user_'}</span>
                  </div>
                </div>
              </Link>
            )}

            <div className="mb-4 pl-2">
              <ProfitInfo variant="menu" onNavigate={closeSidebar} />
            </div>

            {/* disableCollapse: collapse-стейт — про десктопный Sidebar; без
                флага свёрнутость протекала в шторку (иконки без подписей). */}
            <MenuList className="mb-4" disableCollapse />

            <button
              type="button"
              className="mt-auto flex max-h-8 w-full items-center gap-2 py-1 pl-2 text-left text-sm font-medium leading-5 text-content-primary hover:text-content-accent"
              onClick={logoutClickHandler}
            >
              <Icon name="Log_Out" color="currentColor" />
              <span>{t('logout')}</span>
            </button>
          </Drawer.Content>
        </Drawer.Popup>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default MobileSidebar;
